const DEFAULT_THEME = {
  primary: "#1252D2",
  secondary: "#386EDA",
};

// Calculate luminance of a color (0-1, where 1 is white, 0 is black)
const calculateLuminance = (hexColor) => {
  const rgb = parseInt(hexColor.slice(1), 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = (rgb >> 0) & 0xff;

  // Standard luminance formula
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};

// Get contrasting text color based on background luminance
const getContrastingTextColor = (backgroundColor) => {
  const luminance = calculateLuminance(backgroundColor);
  // If background is light, return dark text; if dark, return light text
  return luminance > 0.5 ? "#000000" : "#FFFFFF";
};

const TEAM_COLORS_BY_NAME = {
  "atlanta hawks": { primary: "#E03A3E", secondary: "#C1D32F" },
  "boston celtics": { primary: "#007A33", secondary: "#BA9653" },
  "brooklyn nets": { primary: "#000000", secondary: "#FFFFFF" },
  "charlotte hornets": { primary: "#1D1160", secondary: "#00788C" },
  "chicago bulls": { primary: "#CE1141", secondary: "#000000" },
  "cleveland cavaliers": { primary: "#6F263D", secondary: "#FFB81C" },
  "dallas mavericks": { primary: "#00538C", secondary: "#B8C4CA" },
  "denver nuggets": { primary: "#0E2240", secondary: "#FEC524" },
  "detroit pistons": { primary: "#C8102E", secondary: "#1D42BA" },
  "golden state warriors": { primary: "#1D428A", secondary: "#FFC72C" },
  "houston rockets": { primary: "#CE1141", secondary: "#000000" },
  "indiana pacers": { primary: "#002D62", secondary: "#FDBB30" },
  "los angeles clippers": { primary: "#C8102E", secondary: "#1D428A" },
  "los angeles lakers": { primary: "#552583", secondary: "#FDB927" },
  "memphis grizzlies": { primary: "#12173F", secondary: "#5D76A9" },
  "miami heat": { primary: "#98002E", secondary: "#000000" },
  "milwaukee bucks": { primary: "#00471B", secondary: "#EEE1C6" },
  "minnesota timberwolves": { primary: "#0C2340", secondary: "#236192" },
  "new orleans pelicans": { primary: "#0C2340", secondary: "#C8102E" },
  "new york knicks": { primary: "#006BB6", secondary: "#F58426" },
  "oklahoma city thunder": { primary: "#007AC1", secondary: "#EF3B24" },
  "orlando magic": { primary: "#0077C0", secondary: "#C4CED4" },
  "philadelphia 76ers": { primary: "#006BB6", secondary: "#ED174C" },
  "phoenix suns": { primary: "#1D1160", secondary: "#E56020" },
  "portland trail blazers": { primary: "#E03A3E", secondary: "#000000" },
  "sacramento kings": { primary: "#5A2D81", secondary: "#63727A" },
  "san antonio spurs": { primary: "#000000", secondary: "#C4CED4" },
  "toronto raptors": { primary: "#CE1141", secondary: "#000000" },
  "utah jazz": { primary: "#002B5C", secondary: "#F9A01B" },
  "washington wizards": { primary: "#002B5C", secondary: "#E31837" },
};

const TEAM_COLORS_BY_ID = {
  team_utah: TEAM_COLORS_BY_NAME["utah jazz"],
  team_boston: TEAM_COLORS_BY_NAME["boston celtics"],
  team_portland: TEAM_COLORS_BY_NAME["portland trail blazers"],
  team_brooklyn: TEAM_COLORS_BY_NAME["brooklyn nets"],
  team_minnesota: TEAM_COLORS_BY_NAME["minnesota timberwolves"],
  team_detroit: TEAM_COLORS_BY_NAME["detroit pistons"],
  team_memphis: TEAM_COLORS_BY_NAME["memphis grizzlies"],
  team_new_orleans: TEAM_COLORS_BY_NAME["new orleans pelicans"],
  team_dallas: TEAM_COLORS_BY_NAME["dallas mavericks"],
  team_milwaukee: TEAM_COLORS_BY_NAME["milwaukee bucks"],
  team_miami: TEAM_COLORS_BY_NAME["miami heat"],
  team_chicago: TEAM_COLORS_BY_NAME["chicago bulls"],
  team_orlando: TEAM_COLORS_BY_NAME["orlando magic"],
  team_new_york: TEAM_COLORS_BY_NAME["new york knicks"],
  team_washington: TEAM_COLORS_BY_NAME["washington wizards"],
  team_houston: TEAM_COLORS_BY_NAME["houston rockets"],
  team_indiana: TEAM_COLORS_BY_NAME["indiana pacers"],
  team_charlotte: TEAM_COLORS_BY_NAME["charlotte hornets"],
  team_san_antonio: TEAM_COLORS_BY_NAME["san antonio spurs"],
  team_san_francisco: TEAM_COLORS_BY_NAME["golden state warriors"],
  team_sacramento: TEAM_COLORS_BY_NAME["sacramento kings"],
  team_denver: TEAM_COLORS_BY_NAME["denver nuggets"],
  team_los_angeles_1: TEAM_COLORS_BY_NAME["los angeles lakers"],
  team_philadelphia: TEAM_COLORS_BY_NAME["philadelphia 76ers"],
  team_toronto: TEAM_COLORS_BY_NAME["toronto raptors"],
  team_atlanta: TEAM_COLORS_BY_NAME["atlanta hawks"],
  team_phoenix: TEAM_COLORS_BY_NAME["phoenix suns"],
  team_cleveland: TEAM_COLORS_BY_NAME["cleveland cavaliers"],
  team_oklahoma_city: TEAM_COLORS_BY_NAME["oklahoma city thunder"],
  team_los_angeles_2: TEAM_COLORS_BY_NAME["los angeles clippers"],
};

const TEAM_NAME_ALIASES = {
  hawks: "atlanta hawks",
  celtics: "boston celtics",
  nets: "brooklyn nets",
  hornets: "charlotte hornets",
  bulls: "chicago bulls",
  cavaliers: "cleveland cavaliers",
  mavericks: "dallas mavericks",
  nuggets: "denver nuggets",
  pistons: "detroit pistons",
  warriors: "golden state warriors",
  rockets: "houston rockets",
  pacers: "indiana pacers",
  clippers: "los angeles clippers",
  lakers: "los angeles lakers",
  grizzlies: "memphis grizzlies",
  heat: "miami heat",
  bucks: "milwaukee bucks",
  timberwolves: "minnesota timberwolves",
  pelicans: "new orleans pelicans",
  knicks: "new york knicks",
  thunder: "oklahoma city thunder",
  magic: "orlando magic",
  "76ers": "philadelphia 76ers",
  sixers: "philadelphia 76ers",
  suns: "phoenix suns",
  "trail blazers": "portland trail blazers",
  kings: "sacramento kings",
  spurs: "san antonio spurs",
  raptors: "toronto raptors",
  jazz: "utah jazz",
  wizards: "washington wizards",
};

const normalizeTeamName = (name) => {
  if (!name || typeof name !== "string") {
    return "";
  }

  return name
    .toLowerCase()
    .replace(/[.']/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

const resolveNameToTheme = (rawName) => {
  const normalizedName = normalizeTeamName(rawName);
  if (!normalizedName) {
    return null;
  }

  if (TEAM_COLORS_BY_NAME[normalizedName]) {
    return TEAM_COLORS_BY_NAME[normalizedName];
  }

  const aliasTarget = TEAM_NAME_ALIASES[normalizedName];
  if (aliasTarget && TEAM_COLORS_BY_NAME[aliasTarget]) {
    return TEAM_COLORS_BY_NAME[aliasTarget];
  }

  return null;
};

export const getTeamTheme = (teamInput) => {
  if (!teamInput) {
    return DEFAULT_THEME;
  }

  if (typeof teamInput === "string") {
    return resolveNameToTheme(teamInput) || DEFAULT_THEME;
  }

  const teamIdTheme = TEAM_COLORS_BY_ID[teamInput._id];
  if (teamIdTheme) {
    return teamIdTheme;
  }

  const combinedNameTheme = resolveNameToTheme(`${teamInput.city || ""} ${teamInput.name || ""}`);
  if (combinedNameTheme) {
    return combinedNameTheme;
  }

  const nameTheme = resolveNameToTheme(teamInput.name);
  if (nameTheme) {
    return nameTheme;
  }

  return DEFAULT_THEME;
};

export const applyTeamTheme = (teamInput) => {
  if (typeof document === "undefined") {
    return;
  }

  const { primary, secondary } = getTeamTheme(teamInput);
  const root = document.documentElement;

  root.style.setProperty("--color-primary-override", primary);
  root.style.setProperty("--color-secondary-override", secondary);
  
  // Calculate contrasting text colors based on primary and secondary colors
  const primaryTextColor = getContrastingTextColor(primary);
  const secondaryTextColor = getContrastingTextColor(secondary);
  
  // Set CSS variables for dynamic text colors
  // The third parameter sets the priority (important)
  root.style.setProperty("--team-primary-text", primaryTextColor, "important");
  root.style.setProperty("--team-secondary-text", secondaryTextColor, "important");
};

export const clearTeamTheme = () => {
  if (typeof document === "undefined") {
    return;
  }

  const root = document.documentElement;
  root.style.removeProperty("--color-primary-override");
  root.style.removeProperty("--color-secondary-override");
  root.style.removeProperty("--team-primary-text");
  root.style.removeProperty("--team-secondary-text");
};
